<template>
  <div class="auth-page">
    <div class="auth-page__content">
      <img src="@/assets/images/header-logo.png" alt="Lamar Health" class="auth-page__logo" />
      <h1 class="auth-page__title">Login</h1>

      <div class="auth-page__card">
        <div v-if="!secondVerificationStep">
          <v-form @submit.prevent="loginUser">
            <v-text-field
              v-model="credentials.email"
              label="Email Address"
              type="email"
              placeholder="example@findsunrise.com"
              :error-messages="!isEmailValid && formButtonClicked ? 'Please enter a valid email address' : ''"
              class="mb-4 lh--force-white-background" />

            <v-text-field
              v-model="credentials.password"
              label="Password"
              :type="showPassword ? 'text' : 'password'"
              :error-messages="getPasswordErrorMessage"
              class="mb-4 lh--force-white-background"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword" />

            <v-btn color="primary" block size="large" type="submit" class="mb-4" :loading="showPreloader">
              Login
            </v-btn>

            <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mb-4">
              <div v-for="error in errors" :key="error">{{ error }}</div>
            </v-alert>

            <div class="d-flex flex-column align-center mt-6">
              <router-link
                :to="{ name: 'password-reset-request' }"
                class="text-decoration-none mb-2 text-primary weight-500">
                Forgot Password?
              </router-link>
            </div>
          </v-form>
        </div>

        <div v-else>
          <v-form @submit.prevent="verifyOtp">
            <p class="text-center mb-4 text-primary weight-500">Check your email, enter One Time Password (OTP):</p>
            <v-otp-input
              v-model="otpCode"
              class="mb-5 mt-8 lh--force-white-background"
              length="6"
              color="surface"
              :disabled="showPreloader" />

            <v-btn
              color="primary"
              block
              size="large"
              type="submit"
              class="mb-4"
              :loading="showPreloader"
              :disabled="!otpCode || otpCode.length !== 6">
              Verify OTP
            </v-btn>

            <v-alert v-if="errors.length > 0" type="error" variant="tonal">
              <div v-for="error in errors" :key="error">{{ error }}</div>
            </v-alert>
          </v-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";
import { mainServices } from "@/services/mainServices";

const secondVerificationStep = ref(false);
const otpCode = ref("");
const errors = ref([]);
const formButtonClicked = ref(false);
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const showPreloader = ref(false);
const showPassword = ref(false);

// Add watcher for otpCode
watch(otpCode, (newValue) => {
  // Automatically submit when OTP is fully entered (6 digits)
  if (newValue && newValue.length === 6) {
    verifyOtp();
  }
});

const redirectAfterLogin = async () => {
  const redirectPath = route.query.redirect;
  if (redirectPath) {
    router.push(redirectPath);
    return;
  }
  // Provider logins (LAM-5263) have no company, so there is no client config to consult.
  if (authStore.userInfo?.isProvider) {
    router.push({ name: "prior-auth" });
    return;
  }
  try {
    const config = await mainServices.getCurrentUserClientConfig();
    const page = config?.uiConfig?.defaultPostLoginPage || "all-submissions";
    router.push({ name: page });
  } catch {
    router.push({ name: "all-submissions" });
  }
};

const credentials = ref({
  email: "",
  password: "",
});

// Validators
const isEmailValid = computed(() => {
  const email = credentials.value.email;
  return email !== "" && FORM_VALIDATION_PATTERNS.EMAIL_PATTERN.test(email);
});

const isPasswordValid = computed(() => {
  const password = credentials.value.password;
  return FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password);
});

const getPasswordErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!credentials.value.password) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const loginUser = async () => {
  formButtonClicked.value = true;
  if (isEmailValid.value && isPasswordValid.value) {
    showPreloader.value = true;
    try {
      let otpConfig = await authStore.login(credentials.value);
      if (otpConfig.secondStep) {
        secondVerificationStep.value = true;
      } else {
        await authStore.loginStep2NotRequired();
        await redirectAfterLogin();
      }
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    }
    showPreloader.value = false;
  }
};

const verifyOtp = async () => {
  if (!otpCode.value || otpCode.value.length !== 6) return;

  showPreloader.value = true;
  try {
    await authStore.loginStep2(otpCode.value);
    await redirectAfterLogin();
    secondVerificationStep.value = false;
    otpCode.value = ""; // Clear OTP after successful verification
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    showPreloader.value = false;
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";
@import "../styles/pages/_authorisation-forms.scss";

.auth-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #ffffff;
}

.auth-page__logo {
  height: 36px;
  width: auto;
  margin-bottom: 32px;
}

.auth-page__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
}

.auth-page__title {
  font-family: "DM Sans", sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #052e5e;
  margin-bottom: 32px;
}

.auth-page__card {
  width: 100%;
  max-width: 520px;
  background: #f4f6fa;
  border-radius: 16px;
  padding: 32px;
}

@media (max-width: 600px) {
  .auth-page__content {
    padding: 0 16px 24px;
  }

  .auth-page__logo {
    height: 32px;
    margin-bottom: 24px;
  }

  .auth-page__title {
    font-size: 26px;
    margin-bottom: 24px;
  }

  .auth-page__card {
    max-width: 420px;
    padding: 24px 20px;
  }
}
</style>
